// Helper to convert hex to RGB
const hexToRgb = (hex: string): [number, number, number] => {
    const bigint = parseInt(hex.slice(1), 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
};

// Helper to convert RGB to hex
const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${((1 << 24) | (r << 16) | (g << 8) | b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;
};

function rgbaStringToArray(rgba: string): number[] {
    // Regular expression to extract numbers (including decimals for alpha)
    const result = rgba.match(/\d+(\.\d+)?/g);

    if (!result || (result.length !== 4 && result.length !== 3)) {
        throw new Error("Invalid RGBA format");
    }

    // Convert all extracted values to numbers
    const array = result.map((num) => {
        const floatNum = parseFloat(num);
        return Number.isNaN(floatNum) ? 1 : floatNum;
    });
    return array;
}

function isValidRgba(rgba: string): boolean {
    // Regular expression to match the RGBA format
    const rgbaRegex = /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|0?\.\d+|1(\.0)?)\s*\)$/;

    // Test the string against the regex
    if (!rgbaRegex.test(rgba)) {
        return false;
    }

    // Extract numbers and alpha value
    const result = rgba.match(/\d+(\.\d+)?/g);
    if (!result || result.length !== 4) {
        return false;
    }

    // Parse RGB and alpha values
    const [r, g, b, a] = result.map((num) => parseFloat(num));

    // Validate RGB values (0-255) and alpha value (0-1)
    return (
        r >= 0 && r <= 255 &&
        g >= 0 && g <= 255 &&
        b >= 0 && b <= 255 &&
        a >= 0 && a <= 1
    );
}

export default function generateShadeScale(color: string, steps: number, position: number): string[] {
     // Adjust lightness by a factor
     const adjustLightness = (r: number, g: number, b: number, factor: number): [number, number, number] => {
        const adjust = (value: number) => Math.min(255, Math.max(0, Math.round(value * factor)));
        return [adjust(r), adjust(g), adjust(b)];
    };

    // Validate input
    if (position < 1 || position > steps) {
        throw new Error("Position must be between 1 and the total number of steps.");
    }

    const rgb = isValidRgba(color) ? rgbaStringToArray(color) : hexToRgb(color); // Convert base color to RGB
    const scale: string[] = [];
    const totalLighter = position - 1; // Number of lighter shades
    const totalDarker = steps - position; // Number of darker shades

    // Generate lighter shades
    for (let i = totalLighter; i > 0; i--) {
        const factor = 1 + (i / totalLighter) * 0.5; // Scale lightness factor dynamically
        const [r, g, b] = adjustLightness(rgb[0], rgb[1], rgb[2], factor);
        scale.unshift(rgbToHex(r, g, b)); // Add to the beginning of the array
    }

    // Add the base color at the specified position
    scale.push(color);

    // Generate darker shades
    for (let i = 1; i <= totalDarker; i++) {
        const factor = 1 - (i / totalDarker) * 0.5; // Scale darkness factor dynamically
        const [r, g, b] = adjustLightness(rgb[0], rgb[1], rgb[2], factor);
        scale.push(rgbToHex(r, g, b)); // Add to the end of the array
    }

    return scale;
}
